// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

import { defineStore } from "pinia";
import { useToastStore, Toast } from "@/stores/toast";
import {
	upsertLoadingToast,
	replaceWithTerminalToast,
} from "@/stores/progressToast";
import { triggerDownload } from "@/Utils/triggerDownload";
import ExportStatus = App.Enums.ExportStatus;

// -----------------------------------------------------------------------------

/**
 * Payload broadcast by the backend `ExportProgressUpdated` event
 * (see ExportProgressUpdated::broadcastWith()).
 */
export type ExportProgressEvent = {
	uuid: string;
	status: ExportStatus;
	current_step: number | null;
	step_count: number | null;
	step_name: string | null;
	download_url: string;
};

interface ExportStoreState {
	progressByUuid: Record<string, ExportProgressEvent>;
}

// -----------------------------------------------------------------------------

export const useExportStore = defineStore({
	id: "exportStore",
	state: (): ExportStoreState => ({
		progressByUuid: {},
	}),
	getters: {
		all: (state): ExportProgressEvent[] => Object.values(state.progressByUuid),
	},
	actions: {
		applyProgress(event: ExportProgressEvent) {
			this.progressByUuid[event.uuid] = event;
		},
		clear(uuid: string) {
			delete this.progressByUuid[uuid];
		},
	},
});

// -----------------------------------------------------------------------------

/**
 * Central handler for incoming `ExportProgressUpdated` broadcasts. Records the
 * latest progress and surfaces a toast when an export reaches a terminal state.
 *
 * The completion details and download link are delivered separately through the
 * user's notifications (the backend NotifyStep), so the toast is informational.
 */
export const handleExportProgressEvent = (event: ExportProgressEvent) => {
	useExportStore().applyProgress(event);

	const toasts = useToastStore();

	const loadingId = `export-${event.uuid}`;
	const terminalId = `export-${event.uuid}-done`;

	const humanStep = (): string => {
		if (event.current_step === null || event.step_count === null) {
			return "Preparing your export…";
		}

		const label = event.step_name?.replaceAll("_", " ") ?? "";

		return `Step ${event.current_step + 1} of ${event.step_count}${
			label ? ` · ${label}` : ""
		}`;
	};

	const showLoading = (title: string) =>
		upsertLoadingToast(loadingId, { title, message: humanStep() });

	const showTerminal = (
		data: Pick<Toast, "type" | "title" | "message" | "timeout" | "onClick">
	) => replaceWithTerminalToast(loadingId, terminalId, data);

	const present: Record<ExportStatus | "downloaded", () => void> = {
		pending: () => showLoading("Export queued"),
		running: () => showLoading("Exporting…"),
		success: () =>
			showTerminal({
				type: "success",
				title: "Export ready",
				message: "Your export is ready — click to download.",
				onClick: () => triggerDownload(event.download_url),
			}),
		downloaded: () => {
			toasts.clearById(terminalId);
		},
		failed: () =>
			showTerminal({
				type: "error",
				title: "Export failed",
				message:
					"Something went wrong while preparing your export. Please try again.",
				timeout: 8000,
			}),
		cancelled: () =>
			showTerminal({
				type: "info",
				title: "Export cancelled",
				message: "Your export was cancelled.",
				timeout: 5000,
			}),
	};

	present[event.status]?.();
};
